import type { Bonus } from '../api/fetchers/bonusPromotions/types/bonus';
import type { UserInfoType } from '../api/fetchers/userInfo/types';
import type { CoreRootState } from '../store/CoreStore';
import { ChipTypes } from '../store/slices/chips/types';
import type { ChipValueType } from '../store/slices/chips/types';

// Finds the largest chip from chip stack based on the available balance
export const selectMaxAffordableChip = (
  chips: ChipValueType[],
  selectedChip: ChipValueType,
  userInfo: UserInfoType | null,
  totalBet: number = 0
): ChipValueType => {
  const balance = userInfo?.balance.amount ?? 0;
  const bonus = userInfo?.balance.bonus ?? 0;
  const availableBalance = balance + bonus - totalBet;
  selectedChip = selectedChip ?? chips[0];
  const { type: chipType } = selectedChip;
  const shouldShiftChip = selectedChip.value > availableBalance; // condition to shift active chip left

  // Only process logic for Casino Chips
  if (chipType === ChipTypes.CasinoChip && shouldShiftChip) {
    // Find the largest affordable casino chip
    for (let i = chips.length - 1; i >= 0; i--) {
      const chip = chips[i] as ChipValueType;
      if (chip.type === ChipTypes.CasinoChip && availableBalance >= chip.value) {
        return chip;
      }
    }
    // If no chips are affordable, return the smallest casino chip
    const smallestCasinoChip = chips.find((chip) => chip.type === ChipTypes.CasinoChip);
    return smallestCasinoChip ?? selectedChip; // Fallback to selectedChip if no casino chip exists
  }

  // Return the selected chip for all other cases.
  return selectedChip;
};

export const getChipIndexFromWeight = (weight: number): number => weight - 1;

export const getChipWeightFromIndex = (index: number): number => index + 1;

export const getChipFromValue = (
  chips: ChipValueType[],
  value: number,
  type: ChipTypes = ChipTypes.CasinoChip
): ChipValueType => {
  for (let i = chips.length - 1; i >= 0; i--) {
    const chip = chips[i];
    if (chip && value >= chip.value && type === chip.type) {
      return chip;
    }
  }

  return chips[0] ?? { value: 0, weight: 1, color: undefined, type: ChipTypes.CasinoChip };
};

export const getChipFallbackWeight = (chipAmounts: number[], targetValue: number): number => {
  const index = chipAmounts.findIndex((value) => value >= targetValue);
  const weight = index === -1 ? chipAmounts.length : index + 1;

  return weight;
};

export const getTotalBalanceFromState = (state: CoreRootState): number => {
  const balance = state.gameState.userInfo?.balance;
  return balance ? balance.amount + balance.bonus : 0;
};

export const getChipId = (state: ChipValueType): string => {
  return state.type + state.weight;
};

export const isMatchingChips = (chip1: ChipValueType, chip2: ChipValueType): boolean => {
  if (chip1 && chip2) {
    return chip1.type === chip2.type && chip1.weight === chip2.weight;
  }
  return false;
};

export const computeFreeChipBalance = (bonus?: Bonus): number => {
  if (!bonus) {
    return 0;
  }
  const { chipValue, maxBetLimit, noOfchipsRemaining } = bonus;
  return chipValue * Math.min(noOfchipsRemaining, maxBetLimit);
};
